import { Client, IMessage } from '@stomp/stompjs';

import { formatTopicId, formatTopicPath } from '../utils';
import { ISocketTopic } from './types';

export enum EUserMessageType {
  NOTIFICATION = 'NOTIFICATION'
}

export interface IUserNotificationMessageData {
  T: number; // time
}

export interface IUserNotificationMessage {
  M: EUserMessageType.NOTIFICATION;
  D: IUserNotificationMessageData;
}

export interface IUserReceiver {
  (payload: IUserNotificationMessage | undefined): void;
}

interface ISubscribeParams {
  traderSubscription: string;
  receiver: IUserReceiver;
}

interface IUserProps {
  client: Client;
}

class UserTopic implements ISocketTopic {
  private client: Client;
  private receivers: Record<string, Set<IUserReceiver> | undefined> = {};

  constructor(props: IUserProps) {
    const { client } = props;

    this.client = client;
  }

  public subscribe = (params: ISubscribeParams) => {
    const { traderSubscription, receiver } = params;

    const { subPath, topicId } = this.mapTopicIds(traderSubscription);

    if (!this.client) {
      return;
    }

    if (this.receivers[subPath]) {
      this.receivers[subPath]?.add(receiver);
    } else {
      this.receivers[subPath] = new Set([receiver]);

      try {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message), {
          id: topicId
        });
      } catch (err) {}
    }
  };

  public unsubscribe = (params: ISubscribeParams) => {
    const { traderSubscription, receiver } = params;

    const { subPath, topicId } = this.mapTopicIds(traderSubscription);

    if (!this.client) {
      return;
    }

    this.receivers[subPath]?.delete(receiver);

    if (this.receivers[subPath]?.size === 0) {
      try {
        this.client.unsubscribe(topicId);
      } catch (err) {}

      this.receivers[subPath] = undefined;
    }
  };

  public reconnect = () => {
    const { receivers } = this;

    if (receivers) {
      Object.keys(receivers).forEach((subPath) => {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message), {
          id: formatTopicId(subPath)
        });
      });
    }
  };

  public clearSubscriptions = () => {
    this.receivers = {};
  };

  private onReceive = (id: string, message: IMessage) => {
    const receivers = this.receivers[id];

    if (!receivers || !message) {
      return;
    }

    const payload = JSON.parse(message.body) as IUserNotificationMessage;

    receivers.forEach((receiver) => receiver(payload));
  };

  private mapTopicIds = (traderSubscription: string) => {
    const subPath = `user/${traderSubscription}`;

    return {
      subPath,
      topicId: formatTopicId(subPath)
    };
  };
}

export default UserTopic;
